import { invalidateCapacityContexts } from "~/utils/capacityInvalidation"
import { MinPriorityQueue } from "@datastructures-js/priority-queue"
import { sumBy } from "lodash"
import { Link } from "@tanstack/react-router"
import { useMemo } from "react"
import { toast } from "react-toastify"

import { useCompany } from "~/hooks/useCompany"
import { api } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import { isExtendedStockOutResult } from "~/utils/calculateStockoutTimes"
import { filterDefined, filterNotUndefinedOrNull, groupBy, uniq } from "~/utils/tsUtils"

import { useForecasts } from "./useForecasts"
import { trackEvent } from "./useTrackingService"
import { useVariantMap } from "~/hooks/useVariants"
import { type POSkuData } from "~/hooks/usePurchaseOrdersV2"
import { useStockOutTimesV2 } from "~/hooks/useStockOutTimesV2"
export const useGetPurchaseOrder = (purchaseOrderId: number | undefined) =>
  api.purchaseOrder.get.useQuery(
    { purchaseOrderId: purchaseOrderId ?? 0 },
    { enabled: !!purchaseOrderId },
  ) ?? undefined

export const useGetPurchaseOrders = (status?: string, showArchived?: boolean) =>
  api.purchaseOrder.getAll.useQuery({ status, showArchived })

// Shared invalidation set for any PO mutation that may affect incoming-order tables/maps.
// Extend here to keep the six PO mutations below in sync.
const invalidateIncomingOrderQueries = (utils: ReturnType<typeof api.useUtils>) => {
  utils.variantTableData.variants.invalidate()
  utils.productTableData.stockOutTimesV2.invalidate()
  utils.variantTableData.incomingOrders.invalidate()
  utils.variantTableData.incomingOrderDetailsMap.invalidate()
  utils.variantTableData.incomingOrdersDetail.invalidate()
  utils.sku.incomingOrders.invalidate()
  utils.purchaseOrder.getAll.invalidate()
  utils.purchaseOrder.getAvailabilityViolations.invalidate()
  utils.supplyPlan.getBuy.invalidate()
  utils.purchaseOrder.getOrderSummaryBySupplyPlan.invalidate()
  utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate()
  utils.stockOutTimes.getSuggestedOrders.invalidate()
  // PO status/qty changes affect the "already purchased on sent POs" comparison used by the
  // fashion Buy view — without this, the "some variants are part of sent POs" indicator only
  // updates after a hard refresh.
  utils.purchasePlan.getPurchasedQuantities.invalidate()
  // Same for the Buy Plan list's Qty Ordered / Total Supply / Status columns, which exclude
  // archived and draft POs — archiving or restoring a PO changes all three.
  utils.purchasePlan.getQuantityOrderedForPlans.invalidate()
  utils.purchasePlan.getQtyPlannedDataForPlans.invalidate()
  // Same reason for the PO links behind that figure (HAK-2288): a newly sent PO must appear in the
  // "Already Purchased" hover, not just in its total.
  utils.purchasePlanPurchaseOrder.getLinesBySkuWarehouse.invalidate()
  invalidateCapacityContexts(utils)
  // Otherwise the Availability by category card only updates via the LISTEN/NOTIFY round-trip.
  utils.stockOutTimes.getProjectedStockForPeriods.invalidate()
}

export const useAddItemsToPurchaseOrder = () => {
  const utils = api.useUtils()
  const { data: purchaseOrders } = useGetPurchaseOrders()

  return api.purchaseOrder.addItems.useMutation({
    onSuccess: (_, req) => {
      const purchaseOrder = purchaseOrders?.find((x) => x.id == req.purchaseOrderId)
      invalidateIncomingOrderQueries(utils)
      utils.purchaseOrder.get.invalidate()
      toast.success(
        <>
          <p>
            Variants added to purchase order &apos;{purchaseOrder?.name}&apos;. You can access the
            order{" "}
            <Link
              to="/procurement/purchase-orders/update/$purchaseOrderId"
              params={{ purchaseOrderId: req.purchaseOrderId.toString() }}
              onClick={() =>
                trackEvent("Purchase Order Opened", {
                  "Open Purchase Order Source": "Alert",
                })
              }
            >
              <u>here</u>
            </Link>
            .
          </p>
        </>,
      )
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdatePurchaseOrder = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.update.useMutation({
    onSuccess: (_, req) => {
      invalidateIncomingOrderQueries(utils)
      utils.purchaseOrder.get.invalidate({ purchaseOrderId: req.purchaseOrderId })
      // A PO belongs to a buy; its status/items feed the buy detail (BuyTable reads PO
      // statuses to lock cells, items to display quantities). Invalidate the buy query so
      // those reflect changes made from the PO detail page.
      utils.supplyPlan.getBuy.invalidate()
      toast.success("Purchase order updated.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateMultiplePurchaseOrders = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.updateMultiple.useMutation({
    onSuccess: () => {
      invalidateIncomingOrderQueries(utils)
      utils.supplyPlan.getBuy.invalidate()
      toast.success("Purchase orders updated.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdatePurchaseOrderAssignee = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.updateAssignee.useMutation({
    onSuccess: () => {
      utils.purchaseOrder.getAll.invalidate()
      utils.purchaseOrder.get.invalidate()
      toast.success("Assignee updated.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useCreatePurchaseOrder = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.create.useMutation({
    onSuccess: () => {
      invalidateIncomingOrderQueries(utils)
      utils.purchaseOrder.get.invalidate()
      toast.success("Purchase order created.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useCreateMultiplePurchaseOrders = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.createMultiple.useMutation({
    onSuccess: () => {
      invalidateIncomingOrderQueries(utils)
      utils.purchaseOrder.get.invalidate()
      trackEvent("Purchase Orders Created")
      toast.success("Successfully created multiple purchase orders.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export function useSuggestedPurchaseOrder(
  skuIds: string[],
  warehouseIds: string[],
  expectedArrivalDateOfOrder: Date | undefined,
  nextOrderArrivalDate: Date | undefined,
  expectedArrivalDateBySku: Map<string, Date> | undefined = undefined,
  nextOrderArrivalDateBySku: Map<string, Date> | undefined = undefined,
) {
  const variantMap = useVariantMap()

  const variantIds = useMemo(() => {
    if (!variantMap) return []
    const skuIdSet = new Set(skuIds)
    return uniq(
      Array.from(variantMap.values())
        .filter((v) => v.skuIds.some((id) => skuIdSet.has(id)))
        .map((v) => v.variantId),
    )
  }, [skuIds, variantMap])

  const stockoutTimes = useStockOutTimesV2(
    variantIds,
    expectedArrivalDateOfOrder,
    nextOrderArrivalDate,
    undefined,
    expectedArrivalDateBySku,
    true,
    nextOrderArrivalDateBySku,
  )

  const { data: company } = useCompany()
  const { getSkuForecastsBetweenDates } = useForecasts()
  // TODO: Is this really needed? everywhere the hook is used, needs to handle the case of multiple warehouses with the return type anyways
  // if (warehouseIds.length > 1) {
  //   throw new Error(
  //     "Multiple warehouses might not supported perfectly. Consider how the edge cases should be handled",
  //   )
  // }

  return useMemo(() => {
    if (!stockoutTimes || !variantMap || !company) {
      return undefined
    }
    const items: {
      skuId: string
      warehouseId: string
      quantity: number
      rawQuantity: number
      missingStockEndOfPeriod: number
      purchasePeriodLostSales: number
    }[] = []

    const rawItems = filterDefined(
      Array.from(stockoutTimes)
        .map(([skuId, stockoutTime]) => {
          return warehouseIds.map((warehouseId) => {
            const data = stockoutTime.details[warehouseId]
            const variant = variantMap.get(data?.variantId ?? "")

            if (!data || !variant || !isExtendedStockOutResult(data)) {
              return undefined
            }

            return {
              skuId,
              variantId: variant.variantId,
              settings: variant.settings,
              // Use the canonical ceiled+clamped suggestion (with MINIMUM_ORDER_PREDICTION floor)
              // so this matches what the Supply Plan reads. The lost-sales floor is already applied
              // upstream in calculateSuggestedOrder, so stockout detection is preserved.
              quantity: data.suggestedOrder,
              warehouseId: warehouseId,
              colliSize: Math.max(variant?.settings?.colliSize ?? company?.colliSize ?? 1, 1),
              suggestedOrder: data.missingStockEndOfPeriod,
              minimumOrderQuantity: Math.max(variant?.settings.minimumOrderQuantity, 1),
              forecastAndSafetyStock:
                getSkuForecastsBetweenDates(
                  skuId,
                  warehouseId ? [warehouseId] : undefined,
                  expectedArrivalDateBySku?.get(skuId) ?? expectedArrivalDateOfOrder ?? null,
                  nextOrderArrivalDateBySku?.get(skuId) ?? nextOrderArrivalDate ?? null,
                ) + data.buffer,
              incomingAmount: 0,
              rawQuantity: data.projectedUnderstock,
              missingStockEndOfPeriod: data.missingStockEndOfPeriod,
              purchasePeriodLostSales: data.purchasePeriodLostSales,
            }
          })
        })
        .flat(),
    )

    Object.entries(
      // @ts-ignore
      groupBy(rawItems, (sku) => sku[company.secondaryMoqGroupVariable as keyof POSkuData]),
    ).forEach(([, itemGroup]) => {
      if (itemGroup.some((sku) => sku.quantity)) {
        const secondaryMoq = Math.max(
          1,
          ...filterNotUndefinedOrNull(itemGroup.map((sku) => sku.settings.secondaryMoq)),
        )
        items.push(
          ...fillupSuggesetedOrder(secondaryMoq, itemGroup).map((item, i) => ({
            ...item,
            rawQuantity: itemGroup[i]?.rawQuantity ?? 0,
            missingStockEndOfPeriod: itemGroup[i]?.missingStockEndOfPeriod ?? 0,
            purchasePeriodLostSales: itemGroup[i]?.purchasePeriodLostSales ?? 0,
          })),
        )
      } else {
        items.push(
          ...itemGroup.map((sku) => ({
            skuId: sku.skuId,
            warehouseId: sku.warehouseId,
            quantity: 0,
            rawQuantity: sku.rawQuantity,
            missingStockEndOfPeriod: sku.missingStockEndOfPeriod,
            purchasePeriodLostSales: sku.purchasePeriodLostSales ?? 0,
          })),
        )
      }
    })

    return items
  }, [
    stockoutTimes,
    warehouseIds,
    variantMap,
    company,
    getSkuForecastsBetweenDates,
    expectedArrivalDateOfOrder,
    nextOrderArrivalDate,
    expectedArrivalDateBySku,
    nextOrderArrivalDateBySku,
  ])
}

export const useArchivePurchaseOrder = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.archive.useMutation({
    onSuccess: (_, variables) => {
      invalidateIncomingOrderQueries(utils)
      toast.success(
        variables.ids.length === 1
          ? "Purchase order archived."
          : `${variables.ids.length} purchase orders archived.`,
      )
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useRestorePurchaseOrder = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.restore.useMutation({
    onSuccess: (_, variables) => {
      // Restoring re-adds the PO's quantities everywhere archiving removed them.
      invalidateIncomingOrderQueries(utils)
      toast.success(
        variables.ids.length === 1
          ? "Purchase order restored."
          : `${variables.ids.length} purchase orders restored.`,
      )
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useHardDeletePurchaseOrder = () => {
  const utils = api.useUtils()

  return api.purchaseOrder.hardDelete.useMutation({
    onSuccess: (_, variables) => {
      utils.purchaseOrder.getAll.invalidate()
      invalidateCapacityContexts(utils)
      toast.success(
        variables.ids.length === 1
          ? "Purchase order permanently deleted."
          : `${variables.ids.length} purchase orders permanently deleted.`,
      )
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export function getNextValue(
  quantity: number,
  colliSize: number,
  minimumOrderQuantity: number,
  addValue: number,
) {
  let nextValue = quantity + addValue
  if (nextValue && nextValue < minimumOrderQuantity) {
    nextValue = minimumOrderQuantity
  }
  nextValue = Math.ceil(nextValue / colliSize) * colliSize
  return nextValue
}

export function fillupSuggesetedOrder(
  amount: number,
  items: {
    skuId: string
    warehouseId: string
    quantity: number
    colliSize: number
    suggestedOrder: number
    forecastAndSafetyStock: number
    minimumOrderQuantity: number
    incomingAmount: number
  }[],
) {
  const newItems = items.map((item) => {
    const quantity = item.quantity
      ? getNextValue(item.quantity, item.colliSize, item.minimumOrderQuantity, 0)
      : 0
    const nextValue = getNextValue(
      quantity,
      item.colliSize,
      item.minimumOrderQuantity,
      item.colliSize,
    )
    return {
      ...item,
      originalQuantity: item.quantity,
      quantity,
      nextValue,
      factor: item.forecastAndSafetyStock
        ? (nextValue + item.incomingAmount - item.suggestedOrder) / item.forecastAndSafetyStock
        : Number.POSITIVE_INFINITY,
    }
  })

  const queue = new MinPriorityQueue<(typeof newItems)[0]>((item) => item.factor)

  newItems.forEach((item) => queue.enqueue(item))

  let orderQuantity = sumBy(newItems, "quantity")
  while (amount > orderQuantity) {
    const item = queue.dequeue()
    if (!item) break
    if (!item.forecastAndSafetyStock) continue
    const addedItems = item.nextValue - item.quantity
    item.quantity = item.nextValue
    item.nextValue = getNextValue(
      item.quantity,
      item.colliSize,
      item.minimumOrderQuantity,
      item.colliSize,
    )
    item.factor = item.forecastAndSafetyStock
      ? (item.nextValue + item.incomingAmount - item.suggestedOrder) / item.forecastAndSafetyStock
      : Number.POSITIVE_INFINITY
    queue.enqueue(item)
    orderQuantity += addedItems
  }
  return newItems.map((item) => ({
    skuId: item.skuId,
    warehouseId: item.warehouseId,
    quantity: item.quantity,
  }))
}
